import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { countries } from 'data';
import {
  InputWithConfirm,
  AutocompleteInputWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  CurrencyInputWithConfirm
} from 'shared-components';
import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import { titleAppend } from '../../../../utilities/head';
import { ApplicationState } from '../../../../store';
import * as StudentActions from '../../../../store/student/actions';
import * as PCIActions from '../../../../store/student/preCasInterview/actions';
import { createNavigateForwardAction, createNavigateToAction } from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/preCasInterview/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../store/ui/actions';

const InterviewQPage3 = () => {
  const dispatch = useDispatch();
  const pageTitle = 'Pre-CAS Questionnaire';
  const currentStep = 3;
  const steps = 4;

  const onNext = () => {
    dispatch(StudentActions.savePreCasInterview(Pages.PAGE3));
    dispatch(createNavigateForwardAction());
  };
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(PCIActions.createUpdatePCIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const {
    workWhileStudy = undefined,
    maxWorkHoursPerWeek = '',
    whyWorkWhileStudy = '',
    whyWorkWhileStudyOther = '',
    affordCourseAndLivingWithoutWorking = undefined,
    affordCourseAndLivingWithoutWorkingNo = '',
    completeDegreeWorkCountry = '',
    expectedSalary = 0,
    furtherStudyInUk = undefined,
    furtherStudyCareerPlans = ''
  } = useSelector((state: ApplicationState) => state.student.preCasInterview);

  const [complete, setComplete] = useState(false);

  const pageConfig = useSelector((state: ApplicationState) => state.config.Interview.Page3);

  const [completeDegreeWorkCountryError, setCompleteDegreeWorkCountryError] = useState('');
  const [countryWorkSuggestions, setCountryWorkSuggestions] = useState([]);
  const countriesList: Array<string> = [...new Set(countries.map(country => country.countryName))];

  useEffect(() => {
    const { complete: isComplete } = getPageStatus({
      workWhileStudy,
      maxWorkHoursPerWeek,
      whyWorkWhileStudy,
      whyWorkWhileStudyOther,
      affordCourseAndLivingWithoutWorking,
      affordCourseAndLivingWithoutWorkingNo,
      completeDegreeWorkCountry,
      expectedSalary,
      furtherStudyInUk,
      furtherStudyCareerPlans
    });
    setComplete(isComplete);
  }, [
    workWhileStudy,
    maxWorkHoursPerWeek,
    whyWorkWhileStudy,
    whyWorkWhileStudyOther,
    affordCourseAndLivingWithoutWorking,
    affordCourseAndLivingWithoutWorkingNo,
    completeDegreeWorkCountry,
    expectedSalary,
    furtherStudyInUk,
    furtherStudyCareerPlans
  ]);

  const onMaxHoursChange = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    if ((Number(value) && value.length < 3) || value === '') {
      onChange(Q.MAX_WORK_HOURS_PER_WEEK, value);
    }
  };

  const onWorkCountryChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = countriesList.filter(country => country.toLowerCase().includes(value.toLowerCase()));

    setCountryWorkSuggestions(suggestions);
    onChange(Q.COMPLETE_DEGREE_WORK_COUNTRY, value);
  };

  const onBlurWorkCountry = (event: React.FormEvent<HTMLInputElement>) => {
    const value = (event && event.currentTarget && event.currentTarget.value.trim()) || '';
    const error = countriesList.includes(value) ? '' : 'Please enter a country';
    setCompleteDegreeWorkCountryError(error);
    onChange(Q.COMPLETE_DEGREE_WORK_COUNTRY, value);
  };

  const onFocusWorkCountry = (event: React.FormEvent<HTMLInputElement>) => {
    if (completeDegreeWorkCountryError !== '') {
      setCompleteDegreeWorkCountryError('');
    }
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={pageTitle}
    >
      <Helmet>
        <title>
          {`${pageTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <RadioOptionsWithConfirm
        key={Q.WORK_WHILE_STUDY}
        id={Q.WORK_WHILE_STUDY}
        label="Do you plan to work while you study?"
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: workWhileStudy === true
          },
          {
            char: 'N',
            label: 'No',
            selected: workWhileStudy === false
          }
        ]}
        isInline={true}
        inputHelperText={pageConfig.WorkWhileStudy.helpText}
        onClick={(question: IQuestion) => onChange(Q.WORK_WHILE_STUDY, question.label === 'Yes')}
      />

      {workWhileStudy === true && (
        <>
          <InputWithConfirm
            key={Q.MAX_WORK_HOURS_PER_WEEK}
            id={Q.MAX_WORK_HOURS_PER_WEEK}
            value={maxWorkHoursPerWeek}
            label={'Do you know the maximum number of hours per week you are permitted to work on a student visa?'}
            subText={'Enter a two-digit number below'}
            onChange={onMaxHoursChange}
          />

          <RadioOptionsWithConfirm
            label="Why are you planning to work part-time in the UK while you study?"
            id={Q.WHY_WORK_WHILE_STUDY}
            key={Q.WHY_WORK_WHILE_STUDY}
            questions={[
              {
                char: '1',
                label: 'Extra money for recreation',
                selected: whyWorkWhileStudy === 'Extra money for recreation'
              },
              {
                char: '2',
                label: 'For experience in my industry',
                selected: whyWorkWhileStudy === 'For experience in my industry'
              },
              {
                char: '3',
                label: 'To experience working in the UK',
                selected: whyWorkWhileStudy === 'To experience working in the UK'
              },
              {
                char: '4',
                label: 'Other',
                selected: whyWorkWhileStudy === 'Other'
              }
            ]}
            onClick={(question: IQuestion) => onChange(Q.WHY_WORK_WHILE_STUDY, question.label)}
          />

          {whyWorkWhileStudy === 'Other' && (
            <InputWithConfirm
              key={Q.WHY_WORK_WHILE_STUDY_OTHER}
              id={Q.WHY_WORK_WHILE_STUDY_OTHER}
              value={whyWorkWhileStudyOther}
              label={'Please explain'}
              inputHelperText={pageConfig.WorkWhileStudyReason.helpText}
              onChange={(evt: any) => onChange(Q.WHY_WORK_WHILE_STUDY_OTHER, evt.currentTarget.value)}
            />
          )}

          <RadioOptionsWithConfirm
            key={Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING}
            id={Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING}
            label="Are you able to afford your course fees and living expenses without working in the UK?"
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: affordCourseAndLivingWithoutWorking === true
              },
              {
                char: 'N',
                label: 'No',
                selected: affordCourseAndLivingWithoutWorking === false
              }
            ]}
            isInline={true}
            inputHelperText={pageConfig.CourseAndLivingWithoutWorking.helpText}
            onClick={(question: IQuestion) =>
              onChange(Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING, question.label === 'Yes')
            }
          />

          {affordCourseAndLivingWithoutWorking === false && (
            <InputWithConfirm
              key={Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING_NO}
              id={Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING_NO}
              value={affordCourseAndLivingWithoutWorkingNo}
              label={'Please explain'}
              inputHelperText={pageConfig.CourseAndLivingWithoutWorkingReason.helpText}
              onChange={(evt: any) => onChange(Q.AFFORD_COURSE_AND_LIVING_WITHOUT_WORKING_NO, evt.currentTarget.value)}
            />
          )}
        </>
      )}

      <AutocompleteInputWithConfirm
        id="complete-degree-work-country"
        label="What country do you plan to work in when you complete your degree?"
        onChange={onWorkCountryChange}
        value={completeDegreeWorkCountry}
        showErrorText={!!completeDegreeWorkCountryError}
        errorText={completeDegreeWorkCountryError}
        suggestions={countryWorkSuggestions}
        onBlur={onBlurWorkCountry}
        onFocus={onFocusWorkCountry}
      />

      <CurrencyInputWithConfirm
        id="expectedSalary"
        value={expectedSalary}
        onChange={(event: React.FormEvent<HTMLInputElement>) => {
          const value = Number(event.currentTarget.value) || 0;
          onChange(Q.EXPECTED_SALARY, value);
        }}
        label="Once you graduate, how much do you expect to earn per year in your home country?"
        subText={'Please insert an approximate salary figure in GBP (per year)'}
      />

      <RadioOptionsWithConfirm
        label="Do you plan to continue on to further study in the UK after your course is complete?"
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: furtherStudyInUk === true
          },
          {
            char: 'N',
            label: 'No',
            selected: furtherStudyInUk === false
          }
        ]}
        isInline={true}
        id={Q.FURTHER_STUDY_IN_UK}
        key={Q.FURTHER_STUDY_IN_UK}
        onClick={(question: IQuestion) => onChange(Q.FURTHER_STUDY_IN_UK, question.label === 'Yes')}
      />

      {furtherStudyInUk === true && (
        <InputWithConfirm
          key={Q.FURTHER_STUDY_CAREER_PLANS}
          id={Q.FURTHER_STUDY_CAREER_PLANS}
          value={furtherStudyCareerPlans}
          label={'Explain how this fits into your career plans'}
          inputHelperText={pageConfig.FurtherStudyCareerPlan.helpText}
          onChange={(evt: any) => onChange(Q.FURTHER_STUDY_CAREER_PLANS, evt.currentTarget.value)}
        />
      )}
    </QuestionPage>
  );
};

export default InterviewQPage3;
